/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Clock, Play } from 'lucide-react';
import { initials } from '@/lib/utils-cat';
import { cn } from '@/lib/utils';
import { URGENCY_META } from './doctorConstants';

/**
 * Small presentational widgets used by the Doctor Command Center agenda.
 * Extracted from DoctorCommandCenter.jsx (Code Quality refactor).
 */

// ---------- Timeline row ----------
export const TimelineAppointment = ({ appt, isNow, isNext, onOpenRoom, selected, onSelect }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  const dt = new Date(appt.scheduled_at);
  const now = new Date();
  const canJoin = (dt.getTime() - 10 * 60000) <= now.getTime() && now.getTime() <= (dt.getTime() + 60 * 60000);
  const urg = URGENCY_META[appt.urgency || 'routine'];
  const paid = appt.payment_status === 'paid' || appt.payment_status === 'not_required';

  return (
    <div className={cn('relative pl-6', isNow && 'now-line')}>
      <button
        onClick={() => onSelect(appt)}
        className={cn(
          'w-full text-left rounded-xl border p-3 transition-all',
          selected ? 'border-primary shadow-elev-md bg-primary/5' : 'border-border/60 hover:border-primary/40 bg-card',
          isNow && 'ring-2 ring-primary/40'
        )}
        data-testid={`timeline-appt-${appt.id}`}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <Clock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <span className="font-bold text-sm">{dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          <span className={cn('text-[10px] px-2 py-0.5 rounded-full uppercase font-bold', urg.cls)}>{t(urg.label)}</span>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <Avatar className="h-7 w-7">
            <AvatarFallback className="text-[10px]">{initials(appt.patient_name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate">{appt.patient_name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{appt.reason || t('telemedGenericConsultation')}</div>
          </div>
        </div>
        {(isNow || isNext) && paid && canJoin && (
          <div className="mt-2">
            <Button
              size="sm"
              variant="accent"
              onClick={(e) => { e.stopPropagation(); onOpenRoom(appt.id); }}
              className={cn('w-full', isNow && 'pulse-glow')}
              data-testid={`timeline-join-btn-${appt.id}`}
            >
              <Play className="h-3.5 w-3.5" /> {isNow ? t('bookInVisitNow') : t('bookJoinNow')}
            </Button>
          </div>
        )}
      </button>
    </div>
  );
};

// ---------- KPI mini-card ----------
export const KpiCard = ({ label, value, icon: Icon, color = 'primary' }) => (
  <div className={cn('rounded-xl p-4 flex items-center gap-3 bg-card border border-border/60 shadow-elev-sm')}>
    <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0', color === 'primary' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent')}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
      <div className="font-display font-bold text-xl leading-tight">{value}</div>
    </div>
  </div>
);
